import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Upload, Loader2, Trash2, AlertTriangle, CheckCircle, Info, Search,
  ChevronLeft, ChevronRight, Eye, EyeOff, FileSpreadsheet, Shield, SkipForward, Edit
} from 'lucide-react';

const severityColors = { error: 'bg-red-100 text-red-700', warning: 'bg-yellow-100 text-yellow-700', info: 'bg-blue-100 text-blue-700' };
const severityIcons = { error: AlertTriangle, warning: AlertTriangle, info: Info };

export default function AdminDataCleanupPage() {
  const { api } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const selectedColumns = (() => {
    if (!selected?.columns) return [];
    if (Array.isArray(selected.columns)) return selected.columns;
    try { return JSON.parse(selected.columns); } catch { return []; }
  })();
  const [editingCell, setEditingCell] = useState(null);

  useEffect(() => { fetchUploads(); }, []);

  const fetchUploads = async () => {
    try { const r = await api.get('/staging/'); setUploads(r.data); } catch {}
    finally { setLoading(false); }
  };

  const selectUpload = async (u) => {
    setSelected(u); setPage(1); setFilterStatus('all'); setSearch('');
    fetchRows(u.id, 1, 'all', '');
    fetchSummary(u.id);
  };

  const fetchRows = async (uid, pg = 1, st = 'all', s = '') => {
    setLoadingRows(true);
    try {
      const r = await api.get(`/staging/${uid}/rows`, { params: { page: pg, page_size: 50, status: st, search: s } });
      setRows(r.data.rows); setRowsTotal(r.data.total);
    } catch { setRows([]); }
    finally { setLoadingRows(false); }
  };

  const fetchSummary = async (uid) => {
    try { const r = await api.get(`/staging/${uid}/summary`); setSummary(r.data); } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name.replace(/\.(csv|xlsx|xls)$/i, '').replace(/_/g, ' '));
      const r = await api.post('/staging/upload', fd);
      toast.success(`Uploaded: ${r.data.total_rows} rows — ${r.data.clean} clean, ${r.data.flagged} flagged`);
      fetchUploads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const toggleSkip = async (rowId, skip) => {
    await api.patch(`/staging/rows/${rowId}`, { skip });
    fetchRows(selected.id, page, filterStatus, search);
    fetchSummary(selected.id);
  };

  const saveCell = async (rowId, rowData, field, value) => {
    const newData = { ...rowData, [field]: value };
    try {
      await api.patch(`/staging/rows/${rowId}`, { data: newData });
      toast.success('Fixed');
      await fetchRows(selected.id, page, filterStatus, search);
      fetchSummary(selected.id);
    } catch { toast.error('Save failed'); }
    setEditingCell(null);
  };

  const skipAllFlagged = async () => {
    if (!window.confirm('Skip all flagged rows? They won\'t be imported.')) return;
    await api.post(`/staging/${selected.id}/skip-flagged`);
    toast.success('All flagged rows marked as skip');
    fetchRows(selected.id, page, filterStatus, search);
    fetchSummary(selected.id);
  };

  const deleteUpload = async (uid) => {
    if (!window.confirm('Delete this upload and all its data?')) return;
    await api.delete(`/staging/${uid}`);
    toast.success('Deleted');
    setSelected(null); setSummary(null); setRows([]);
    fetchUploads();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Shield className="w-6 h-6" /> Data Cleanup</h1>
          <p className="text-muted-foreground">Upload → detect issues → fix → import clean data</p>
        </div>
        <div>
          <Label htmlFor="cleanup-upload" className="cursor-pointer">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white ${uploading ? 'opacity-50' : 'hover:bg-primary/90 cursor-pointer'}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload File
            </div>
          </Label>
          <Input id="cleanup-upload" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleUpload} disabled={uploading} />
        </div>
      </div>

      {/* Upload selector */}
      {uploads.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {uploads.map(u => (
            <button key={u.id} onClick={() => selectUpload(u)}
              className={`flex-shrink-0 p-3 rounded-lg border-2 text-left transition-all ${
                selected?.id === u.id ? 'border-primary bg-primary/5 shadow-md' : 'border-transparent bg-white hover:border-gray-200'
              }`}>
              <p className="font-semibold text-sm truncate max-w-[200px]">{u.name}</p>
              <div className="flex gap-2 mt-1">
                <Badge className="bg-green-100 text-green-700 text-[10px]">{u.clean_count} clean</Badge>
                {u.flagged_count > 0 && <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">{u.flagged_count} flagged</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{u.row_count} rows</p>
            </button>
          ))}
        </div>
      )}

      {!selected && uploads.length === 0 && (
        <Card><CardContent className="p-16 text-center text-muted-foreground">
          <FileSpreadsheet className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold">Upload a file to start cleaning</p>
          <p className="text-sm">Supports CSV and XLSX. Issues will be auto-detected.</p>
        </CardContent></Card>
      )}

      {selected && summary && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <button onClick={() => { setFilterStatus('all'); setPage(1); fetchRows(selected.id, 1, 'all', search); }}
              className={`p-4 rounded-xl border-2 text-center transition-all ${filterStatus === 'all' ? 'border-primary bg-primary/5 shadow-md' : 'border-transparent bg-white hover:border-gray-200'}`}>
              <p className="text-2xl font-bold">{summary.total}</p><p className="text-xs text-muted-foreground">Total Rows</p>
            </button>
            <button onClick={() => { setFilterStatus('clean'); setPage(1); fetchRows(selected.id, 1, 'clean', search); }}
              className={`p-4 rounded-xl border-2 text-center transition-all ${filterStatus === 'clean' ? 'border-green-500 bg-green-50 shadow-md' : 'border-transparent bg-white hover:border-gray-200'}`}>
              <p className="text-2xl font-bold text-green-600">{summary.clean}</p><p className="text-xs text-muted-foreground">Clean</p>
            </button>
            <button onClick={() => { setFilterStatus('flagged'); setPage(1); fetchRows(selected.id, 1, 'flagged', search); }}
              className={`p-4 rounded-xl border-2 text-center transition-all ${filterStatus === 'flagged' ? 'border-yellow-500 bg-yellow-50 shadow-md' : 'border-transparent bg-white hover:border-gray-200'}`}>
              <p className="text-2xl font-bold text-yellow-600">{summary.flagged}</p><p className="text-xs text-muted-foreground">Flagged</p>
            </button>
            <button onClick={() => { setFilterStatus('skipped'); setPage(1); fetchRows(selected.id, 1, 'skipped', search); }}
              className={`p-4 rounded-xl border-2 text-center transition-all ${filterStatus === 'skipped' ? 'border-gray-500 bg-gray-50 shadow-md' : 'border-transparent bg-white hover:border-gray-200'}`}>
              <p className="text-2xl font-bold text-gray-500">{summary.skipped}</p><p className="text-xs text-muted-foreground">Skipped</p>
            </button>
            <div className="flex flex-col gap-1">
              <Button variant="outline" size="sm" onClick={skipAllFlagged}><SkipForward className="h-3 w-3 mr-1" /> Skip All Flagged</Button>
              <Button variant="destructive" size="sm" onClick={() => deleteUpload(selected.id)}><Trash2 className="h-3 w-3 mr-1" /> Delete Upload</Button>
            </div>
          </div>

          {/* Issue Breakdown */}
          {summary.issues?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {summary.issues.map((iss, i) => {
                const Icon = severityIcons[iss.severity] || Info;
                return (
                  <Badge key={i} className={`${severityColors[iss.severity]} text-xs gap-1`}>
                    <Icon className="h-3 w-3" />{iss.type.replace(/_/g, ' ')} ({iss.count})
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search rows..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); fetchRows(selected.id, 1, filterStatus, e.target.value); }}
              className="pl-10" />
          </div>

          {/* Data Table */}
          <div className="overflow-auto border rounded-lg max-h-[450px]">
            {loadingRows ? <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-8">#</TableHead>
                    <TableHead className="text-xs w-16">Status</TableHead>
                    <TableHead className="text-xs w-8"></TableHead>
                    {selectedColumns.slice(0, 8).map(col => (
                      <TableHead key={col} className="text-xs whitespace-nowrap">{col}</TableHead>
                    ))}
                    <TableHead className="text-xs">Issues</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => {
                    const rowIssues = Array.isArray(row.issues) ? row.issues : (typeof row.issues === 'string' ? JSON.parse(row.issues || '[]') : []);
                    return (
                    <TableRow key={row.id} className={`${row.skip ? 'opacity-40 bg-gray-50 line-through' : row.status === 'flagged' ? 'bg-yellow-50/50' : ''}`}>
                      <TableCell className="text-xs text-muted-foreground">{row.row_number}</TableCell>
                      <TableCell className="text-xs">
                        {row.skip ? <Badge variant="outline" className="text-[10px]">Skip</Badge>
                          : row.status === 'clean' ? <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle className="h-3 w-3 mr-0.5" />Clean</Badge>
                          : <Badge className="bg-yellow-100 text-yellow-700 text-[10px]"><AlertTriangle className="h-3 w-3 mr-0.5" />Flag</Badge>}
                      </TableCell>
                      <TableCell className="p-1">
                        <Button variant="ghost" size="icon" className="h-5 w-5"
                          onClick={() => toggleSkip(row.id, !row.skip)} title={row.skip ? 'Restore' : 'Skip'}>
                          {row.skip ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                        </Button>
                      </TableCell>
                      {selectedColumns.slice(0, 8).map(col => {
                        const val = row.data?.[col] ?? '';
                        const hasIssue = rowIssues.some(i => i.field === col);
                        const isEditing = editingCell?.rowId === row.id && editingCell?.field === col;
                        return (
                          <TableCell key={col} className={`text-xs ${hasIssue ? 'bg-red-50 border-l-2 border-red-300' : ''}`}>
                            {isEditing ? (
                              <Input autoFocus defaultValue={editingCell.value} className="h-6 text-xs p-1 min-w-[80px]"
                                onBlur={e => saveCell(row.id, row.data, col, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveCell(row.id, row.data, col, e.target.value); if (e.key === 'Escape') setEditingCell(null); }} />
                            ) : (
                              <span className={`cursor-pointer hover:bg-blue-50 px-1 rounded truncate block max-w-[150px] ${hasIssue ? 'text-red-700 font-medium' : ''}`}
                                onDoubleClick={() => setEditingCell({ rowId: row.id, field: col, value: val })}
                                title={`${val}\n\nDouble-click to edit`}>{val || '-'}</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-xs">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {rowIssues.map((iss, i) => (
                            <span key={i} className={`text-[9px] px-1 rounded ${severityColors[iss.severity]}`} title={iss.message}>
                              {iss.type.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(rowsTotal / 50)} ({rowsTotal} rows)</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page-1); fetchRows(selected.id, page-1, filterStatus, search); }}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= Math.ceil(rowsTotal / 50)} onClick={() => { setPage(page+1); fetchRows(selected.id, page+1, filterStatus, search); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
